'use client';

import { useEffect, useRef, useState, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { supabase } from '@/lib/supabase/client';
import { Loader2, AlertCircle } from 'lucide-react';
import { errorMsg } from '@/lib/utils/error';
import { useEmployeePermissions } from '@/hooks/useEmployeePermissions';

interface AuthGuardProps {
  children: ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const t = useTranslations('admin.ui');
  const [mounted, setMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  // Recoverable error state: shown when the session check keeps failing on a
  // transient fault (network down / Supabase unreachable) AFTER a retry. We
  // surface a "retry" button instead of an infinite spinner OR a forced
  // logout — a cached-but-unrefreshable session shouldn't nuke the user.
  const [authError, setAuthError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // Once we've confirmed a session, never let a later *re-check* failure
  // (slow getSession during a heavy flow, e.g. Stripe iframe loading) blow
  // away the already-rendered authenticated view. A valid session cookie is
  // still present; rendering doesn't depend on a live getSession round-trip.
  const authedRef = useRef(false);
  const router = useRouter();
  const pathname = usePathname();
  const { canAccessRoute, isLoading: permissionsLoading } = useEmployeePermissions();

  // Prevent hydration mismatch - only render after mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot mount sync
    setMounted(true);
  }, []);

  // Definiere öffentliche Routen, die keine Authentifizierung benötigen
  const publicRoutes = [
    '/admin/login',
    '/admin/register', // falls Sie eine Registrierungsseite haben
    '/admin/forgot-password', // falls Sie eine Passwort-vergessen-Seite haben
  ];

  // Prüfe, ob die aktuelle Route öffentlich ist
  const isPublicRoute = publicRoutes.includes(pathname);

  useEffect(() => {
    let isMounted = true;

    // Wenn es eine öffentliche Route ist, lade die Seite ohne Authentifizierungsprüfung
    if (isPublicRoute) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional
      setIsLoading(false);
      setIsAuthenticated(true); // Setze auf true, damit die Seite angezeigt wird
      return;
    }

    // `getSession()` triggert bei abgelaufenem Token einen Token-Refresh-
    // Netzwerk-Call OHNE eigenen Timeout → kann hängen. Race gegen Timeout,
    // damit der Check IMMER terminiert (sonst ewiger Spinner bis Hard-Reload).
    const getSessionWithTimeout = (ms: number) =>
      Promise.race([
        supabase.auth.getSession(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('auth-check-timeout')), ms),
        ),
      ]);

    const isTransient = (err: unknown) => {
      const msg = errorMsg(err);
      return (
        msg.includes('auth-check-timeout') ||
        msg.includes('fetch failed') ||
        msg.includes('ENOTFOUND') ||
        msg.includes('network') ||
        msg.includes('Failed to fetch')
      );
    };

    // Initialer Gate-Check mit 1× Retry bei transientem Fehler. Terminiert
    // immer in einen von drei klaren Zuständen: authentifiziert · zu Login ·
    // recoverable Error (Retry-Button) — nie Infinite-Spinner, nie Zwangs-
    // Logout bei bloß flakigem Netz.
    const checkAuthentication = async () => {
      if (isMounted) setAuthError(false);
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const { data } = await getSessionWithTimeout(12000);
          if (!isMounted) return;
          if (!data.session) {
            router.push('/admin/login');
            return;
          }
          authedRef.current = true;
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        } catch (error) {
          // Already authenticated → a slow/failed re-check is irrelevant to
          // rendering; swallow it (keep showing the page) instead of nuking
          // the view with the error screen.
          if (authedRef.current) return;
          if (!isTransient(error)) {
            // Echter Auth-Fehler (kein Netz-Problem) → Re-Login.
            if (isMounted) router.push('/admin/login');
            return;
          }
          // Transient: 1× kurz warten + retrien, sonst recoverable Error.
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 1200));
            continue;
          }
          if (isMounted) {
            setAuthError(true);
            setIsLoading(false);
          }
          return;
        }
      }
    };

    checkAuthentication();

    // F-500: auf Auth-Events reagieren statt alle 5 min pollen. Feuert auf
    // SIGNED_OUT (anderer Tab / Session-Revoke), TOKEN_REFRESHED, SIGNED_IN —
    // hält das Gate sofort in Sync. Callback bleibt synchron (kein await auf
    // supabase-Methoden → Supabase-Deadlock-Warnung vermeiden).
    const { data: authSub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      // Only the explicit SIGNED_OUT event forces a redirect. We deliberately
      // do NOT treat a transient `!session` on other events as a logout — that
      // caused false redirects during token refresh. SIGNED_OUT fires on real
      // logout / session-revoke / another tab signing out.
      if (event === 'SIGNED_OUT') {
        authedRef.current = false;
        setIsAuthenticated(false);
        router.push('/admin/login');
      } else if (session) {
        authedRef.current = true;
        setIsAuthenticated(true);
        setIsLoading(false);
        setAuthError(false);
      }
    });

    return () => {
      isMounted = false;
      authSub.subscription.unsubscribe();
    };
  }, [router, pathname, isPublicRoute, retryNonce]);

  // Check employee permissions after authentication
  useEffect(() => {
    if (isAuthenticated && !isLoading && !permissionsLoading) {
      // Skip permission check for public routes
      if (isPublicRoute) {
        return;
      }

      // Check if employee can access current route
      // canAccessRoute returns true for tenant_owners automatically
      const hasAccess = canAccessRoute(pathname);

      if (!hasAccess) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional
        setPermissionDenied(true);
        // Redirect to dashboard after showing error
        setTimeout(() => {
          router.push('/admin?error=insufficient_permissions');
        }, 2000);
      } else {
        // User has access - reset permission denied state
        setPermissionDenied(false);
      }
    }
  }, [isAuthenticated, isLoading, permissionsLoading, pathname, canAccessRoute, router, isPublicRoute]);

  // Prevent hydration mismatch - return null on server/initial render
  if (!mounted) {
    return null;
  }

  // Für öffentliche Routen: zeige sofort den Inhalt
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Loader/Error-Screen NUR vor der ersten erfolgreichen Auth zeigen. Sobald
  // authentifiziert, fällt es zu `children` durch (siehe unten) — ein langsamer
  // Hintergrund-Re-Check darf die Live-Ansicht nicht durch Spinner/Error ersetzen.
  if (isLoading && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-6">
          {/* Animated Logo */}
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-blue-600 shadow-xl">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>

          {/* Loading Text */}
          <div className="space-y-2 text-center">
            <h2 className="text-xl font-semibold text-gray-900">EazyBooking</h2>
            <p className="text-sm text-gray-600">{t('checkingAuth')}</p>
          </div>

          {/* Loading Bar */}
          <div className="h-1 w-48 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full animate-pulse rounded-full bg-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // Recoverable connection error — session check kept failing on a transient
  // fault after a retry. Offer "retry" instead of an infinite spinner / forced
  // logout (a cached session may still be valid once the network recovers).
  if (authError && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="flex max-w-sm flex-col items-center gap-6 px-6 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-amber-100 shadow-xl">
            <AlertCircle className="h-8 w-8 text-amber-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">{t('connectionErrorTitle')}</h2>
            <p className="text-sm text-gray-600">{t('connectionErrorBody')}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsLoading(true);
              setAuthError(false);
              setRetryNonce((n) => n + 1);
            }}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
          >
            <Loader2 className="hidden h-4 w-4 animate-spin" />
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  // Show permission denied message if employee tries to access restricted route
  if (permissionDenied) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-6 text-center">
          {/* Error Icon */}
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-red-100 shadow-xl">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>

          {/* Error Message */}
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">{t('accessDenied')}</h2>
            <p className="text-sm text-gray-600 max-w-md">
              {t('noPagePermission')}
              <br />
              {t('redirectingToDashboard')}
            </p>
          </div>

          {/* Loading Bar */}
          <div className="h-1 w-48 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full animate-pulse rounded-full bg-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // Zeige die Kinder nur an, wenn der Benutzer authentifiziert ist
  return isAuthenticated ? <>{children}</> : null;
}
